import { applyChosenProfileImage, resolveProfileImage } from "./profileImage";
import {
  clearLegacyProfileLocalStorage,
  currentUserKey,
} from "./profileUserStore";
import { avatarIndexFromSrc } from "./profileAvatars";

const SESSION_KEY = "profileData";

const FORM_FIELDS = [
  "firstname",
  "lastname",
  "name",
  "username",
  "bio",
  "gender",
  "email",
  "dateofbirth",
  "dateOfBirth",
  "city",
  "country",
  "phone",
  "social_facebook",
  "social_instagram",
  "social_twitter",
  "social_skype",
  "social_youtube",
  "profileImage",
  "avatar_index",
  "avatarIndex",
  "profile_image",
];

function readJson(store, key) {
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function hasText(value) {
  return value != null && String(value).trim() !== "";
}

export function profileDisplayName(profile) {
  if (!profile) return "";
  const fromParts = [profile.firstname, profile.lastname]
    .filter(hasText)
    .join(" ")
    .trim();
  if (fromParts) return fromParts;
  if (hasText(profile.name)) return String(profile.name).trim();
  if (hasText(profile.username)) return String(profile.username).trim();
  return "";
}

export function unwrapProfile(response) {
  const body = response?.data ?? response;
  if (!body || typeof body !== "object") return null;
  const nested = body.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested;
  }
  return body;
}

/** Normalize API profile fields (snake_case / camelCase) for the UI. */
export function normalizeApiProfile(raw) {
  if (!raw || typeof raw !== "object") return null;
  const profile = { ...raw };
  const image = resolveProfileImage(profile);
  if (image) profile.profileImage = image;

  const index = Number(
    profile.avatar_index ?? profile.avatarIndex ?? avatarIndexFromSrc(image),
  );
  if (Number.isInteger(index) && index >= 0) {
    profile.avatar_index = index;
    profile.avatarIndex = index;
  }

  if (!hasText(profile.dateofbirth) && hasText(profile.dateOfBirth)) {
    profile.dateofbirth = profile.dateOfBirth;
  }
  if (!hasText(profile.dateOfBirth) && hasText(profile.dateofbirth)) {
    profile.dateOfBirth = profile.dateofbirth;
  }

  if (!hasText(profile.firstname) && hasText(profile.name)) {
    const parts = String(profile.name).trim().split(/\s+/);
    profile.firstname = parts[0] || "";
    profile.lastname = parts.slice(1).join(" ");
  }

  const displayName = profileDisplayName(profile);
  if (displayName) profile.name = displayName;

  return applyChosenProfileImage(profile) || profile;
}

/**
 * Session cache of the latest API profile (same tab only).
 * Not persisted in localStorage.
 */
export function getSavedProfileForm() {
  return readSessionProfile();
}

export function mergeProfiles(apiProfile, sessionProfile) {
  const api = normalizeApiProfile(apiProfile) || {};
  const session =
    sessionProfile && typeof sessionProfile === "object" ? sessionProfile : {};
  const merged = { ...session, ...api };

  // Fill blanks from session only when API omitted the field.
  FORM_FIELDS.forEach((key) => {
    if (!hasText(merged[key]) && hasText(session[key])) {
      merged[key] = session[key];
    }
  });

  // Keep optimistic avatar from session (same as mobile ChangePhoto flow).
  // API responses can lag behind a just-picked character.
  const sessionImage = resolveProfileImage(session);
  if (sessionImage) {
    const sessionIdx = Number(
      session.avatar_index ?? session.avatarIndex ?? avatarIndexFromSrc(sessionImage),
    );
    merged.profileImage = sessionImage;
    if (Number.isInteger(sessionIdx) && sessionIdx >= 0) {
      merged.avatar_index = sessionIdx;
      merged.avatarIndex = sessionIdx;
    }
  }

  return normalizeApiProfile(merged) || merged;
}

export function readSessionProfile() {
  const saved = readJson(sessionStorage, SESSION_KEY);
  const key = currentUserKey();
  if (!saved) return null;
  if (key && saved.userKey && saved.userKey !== key) return null;
  return saved;
}

export function saveProfileRecord(profile, { notify = true } = {}) {
  clearLegacyProfileLocalStorage();
  const record = {
    ...normalizeApiProfile(profile),
    userKey: currentUserKey(),
  };
  try {
    const previous = readJson(sessionStorage, SESSION_KEY) || {};
    const base =
      previous.userKey && previous.userKey === record.userKey ? previous : {};
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ ...base, ...record }),
    );
  } catch {
    /* session storage can be unavailable */
  }
  if (notify) window.dispatchEvent(new Event("profileUpdated"));
  return record;
}

export function buildProfilePayload(formData) {
  const avatarIndex = avatarIndexFromSrc(formData?.profileImage);
  const payload = {
    name: profileDisplayName(formData),
    firstname: formData.firstname || "",
    lastname: formData.lastname || "",
    bio: formData.bio || "",
    gender: formData.gender || "",
    email: formData.email || "",
    dateOfBirth: formData.dateofbirth || formData.dateOfBirth || "",
    dateofbirth: formData.dateofbirth || formData.dateOfBirth || "",
    city: formData.city || "",
    country: formData.country || "",
    social_facebook: formData.social_facebook || "",
    social_instagram: formData.social_instagram || "",
    social_twitter: formData.social_twitter || "",
    social_skype: formData.social_skype || "",
    social_youtube: formData.social_youtube || "",
  };
  if (hasText(formData.username)) payload.username = formData.username;

  // Persist character / photo on the profile API (not localStorage).
  if (avatarIndex >= 0) {
    payload.avatar_index = avatarIndex;
    payload.avatarIndex = avatarIndex;
    payload.profile_image = "";
    payload.profileImage = "";
  } else if (hasText(formData.profileImage)) {
    payload.profile_image = formData.profileImage;
    payload.profileImage = formData.profileImage;
    payload.avatar_index = null;
  }

  return payload;
}

export function formFromProfile(data = {}) {
  const merged = normalizeApiProfile(data) || {};
  return {
    username: merged.username || "",
    firstname: merged.firstname || "",
    lastname: merged.lastname || "",
    city: merged.city || "",
    country: merged.country || "",
    bio: merged.bio || "",
    gender: merged.gender || "",
    email: merged.email || "",
    dateofbirth: merged.dateofbirth || merged.dateOfBirth || "",
    phone: merged.phone ? String(merged.phone) : "",
    joinDate: merged.joinDate || "",
    profileImage: merged.profileImage || "",
    avatar_index: merged.avatar_index ?? merged.avatarIndex,
    social_facebook: merged.social_facebook || "",
    social_instagram: merged.social_instagram || "",
    social_twitter: merged.social_twitter || "",
    social_skype: merged.social_skype || "",
    social_youtube: merged.social_youtube || "",
    name: profileDisplayName(merged),
  };
}
